import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { Link, useLocation, withRouter } from "react-router-dom";
import * as RoutePath from "../../shared/utils/routeLink";
import * as Variable from "../../shared/utils/variables";
import * as ActionTypes from "../../store/actions/actionTypes";
import { toast } from "react-toastify";
import "./MajorDetail.scss";
import Image from "../../shared/component/UI/Image";
import {
	BookmarkIcon,
	CalendarBlankIcon,
	ChalkboardTeacherIcon,
	ChromeIcon,
	CreditCardIcon,
	EnvelopeIcon,
	GradCapIconOutline,
	HeartIconFilled,
	HeartIconOutline,
	MoneyOutlineIcon,
	PhoneIcon,
	StarIcon,
	StudentIconOutline,
	TopRightArrowIcon,
} from "../../shared/component/svg/Icons";
import { Carousel } from "react-responsive-carousel";
import "react-responsive-carousel/lib/styles/carousel.min.css";
import LoadingComponent from "../../shared/component/LoadingComponent/LoadingComponent";
import CarouselIndicator from "../../shared/component/CarouselIndicator/CarouselIndicator";
import makeRequest from "../../shared/utils/request";
import { generateRequestOptions } from "../../shared/utils/apiEndPoints";
import { getLocale } from "../../shared/component/Card/Card";
import { orderBy } from "lodash";
import { Helmet } from "react-helmet";
import Anchor from "../../shared/component/UI/Anchor";
import { getCurrencySymbol } from "../../shared/utils/string";

const MajorDetail = ({ ENV_NAME, isAuthenticated, fetchingAddRemoveFavorites, ...props }) => {
	const DEGREE_OPT = {
		"D1": `${Variable.DIPLOMA_1[ENV_NAME]} (D-1)`,
		"D2": `${Variable.DIPLOMA_2[ENV_NAME]} (D-2)`,
		"D3": `${Variable.DIPLOMA_3[ENV_NAME]} (D-3)`,
		"D4": `${Variable.DIPLOMA_4[ENV_NAME]} (D-4)`,
		"S1": `${Variable.UNDERGRAD[ENV_NAME]} (S-1)`,
		"S2": `${Variable.POSTGRAD[ENV_NAME]} (S-2)`,
		"S3": `${Variable.DOCTORAL[ENV_NAME]} (S-3)`,
		"Profession": Variable.PROFESSION[ENV_NAME],
		"Sp-1": `${Variable.SPECIALIST_1[ENV_NAME]} (Sp-1)`,
		"Sp-2": `${Variable.SPECIALIST_2[ENV_NAME]} (Sp-2)`,
	};
	const [isLoading, setIsLoading] = useState(false);
	const [majorData, setMajorData] = useState(null);
	const [achivementIdx, setAchievementIdx] = useState(0);
	let location = useLocation();

	// Get the id of a major
	// If they don't have any major, redirect to the home page
	useEffect(() => {
		if (location?.pathname?.includes(RoutePath.MAJOR_DETAIL)) {
			let url = location?.pathname;
			let id = url?.substring(url.lastIndexOf("/") + 1);
			if (id !== RoutePath.MAJOR_DETAIL && !isNaN(id)) {
				getMajorDetails(id);
			} else {
				toast.error(Variable.MAJOR_NOT_FOUND[ENV_NAME]);
				props.history.push(RoutePath.ROOT);
			}
		}
	}, [location]); // eslint-disable-line

	// Get major details
	// If API error, redirect to the home page
	const getMajorDetails = async (id) => {
		setIsLoading(true);
		let res = await makeRequest(generateRequestOptions("majorDetail", { urlParams: id + "/" }));
		if (res.code === 200) {
			setMajorData(res.data);
		} else {
			toast.error(Variable.MAJOR_NOT_FOUND[ENV_NAME]);
			props.history.push(RoutePath.ROOT);
		}
		setIsLoading(false);
	};

	return (
		<div className="major-detail">
			{majorData?.major_name && (
				<Helmet>
					<title>
						Direktori Kampus Terbaik di Indonesia - Portal Sekolah - {majorData?.major_name || "-"}
					</title>
				</Helmet>
			)}
			{isLoading ? (
				<LoadingComponent />
			) : (
				<>
					<Image className="full-width-banner" src={majorData?.major_image} />
					<div className="container major-header">
						<div className="d-flex justify-content-between align-items-center">
							<div className="d-flex align-items-center">
								<Image src={majorData?.major_icon} className="major-logo" />
								<h2 className="text-label-dark-gray-2 mb-0">
									<strong>{majorData?.major_name || "-"}</strong>
								</h2>
							</div>
							<button
								className="btn fav-button"
								onClick={() => {
									if (fetchingAddRemoveFavorites) return;
									if (isAuthenticated) {
										props.addRemoveFavorite(
											majorData?.is_favourite ? "remove" : "add",
											"major",
											majorData?.is_favourite ? majorData?.fav_obj_id : majorData?.id,
											(res) => {
												if (majorData?.is_favourite) {
													setMajorData((p) => ({
														...p,
														fav_obj_id: null,
														is_favourite: false,
													}));
												} else {
													setMajorData((p) => ({
														...p,
														fav_obj_id: res?.data?.id,
														is_favourite: true,
													}));
												}
											}
										);
									} else {
										props.openLoginModal();
									}
								}}
							>
								{majorData?.is_favourite ? (
									<>
										<HeartIconFilled className="me-xs" width="2rem" height="2rem" fill="#F24822" />
										{Variable.REMOVE_FROM_FAVORITES[ENV_NAME]}
									</>
								) : (
									<>
										<HeartIconOutline
											className="me-xs"
											width="2rem"
											height="2rem"
											stroke="#F24822"
										/>
										{Variable.ADD_TO_FAVORITES[ENV_NAME]}
									</>
								)}
							</button>
						</div>
						<div className="divider" />
						<div className="d-flex-desktop text-center">
							<div className="w-100 px-sm">
								<div className="text-label-dark-gray mb-sm">{Variable.COLLEGE[ENV_NAME]}</div>
								<div className="flex-all-center">
									<Image src={majorData?.college?.college_logo} className="campus-logo" />
									<Link
										className="text-decoration-none"
										to={RoutePath.COLLEGE_DETAIL + "/" + majorData?.college?.id}
									>
										<h4 className="text-label-blue my-xxs">
											<strong>{majorData?.college?.name || "-"}</strong>
										</h4>
									</Link>
								</div>
							</div>
							<div className="w-100 px-sm">
								<div className="text-label-dark-gray mb-sm">{Variable.FACULTY[ENV_NAME]}</div>
								<div className="flex-all-center">
									<h4 className="text-label-dark-gray-2 my-xxs">
										<strong>{majorData?.faculty_category || "-"}</strong>
									</h4>
								</div>
							</div>
							<div className="w-100 px-sm">
								<div className="text-label-dark-gray mb-sm">{Variable.ACCREDITATION[ENV_NAME]}</div>
								<div className="flex-all-center">
									<h4 className="text-label-dark-gray-2 my-xxs">
										<strong>
											{majorData?.abroad_accreditation ||
												majorData?.major_accreditation ||
												Variable.NO_ACCREDITATION[ENV_NAME]}
										</strong>
									</h4>
								</div>
							</div>
						</div>
					</div>
					<section className="container detail-section">
						<h2 className="header text-center">{Variable.MAJOR_DESCRIPTION[ENV_NAME]}</h2>
						<div
							className="force-styling-wysiwyg"
							dangerouslySetInnerHTML={{
								__html: majorData?.major_description || "",
							}}
						/>
					</section>
					<section className="major-general-info detail-section">
						<h2 className="header text-center">{Variable.GENERAL_INFORMATION[ENV_NAME]}</h2>
						<div className="major-info-item">
							<StarIcon className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className="mii-label me-sm">{Variable.ACCREDITATION[ENV_NAME]}</div>
							<div className="mii-item">
								{majorData?.abroad_accreditation ||
									majorData?.major_accreditation ||
									Variable.NO_ACCREDITATION[ENV_NAME]}
							</div>
						</div>
						<div className="major-info-item">
							<CreditCardIcon className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className="mii-label me-sm">{Variable.REGISTRATION_FEE[ENV_NAME]}</div>
							<div className="mii-item">
								{!isNaN(majorData?.registration_fee)
									? Number(majorData?.registration_fee || 0)?.toLocaleString(getLocale(ENV_NAME)) ||
									  "-"
									: majorData?.registration_fee || "-"}
							</div>
						</div>
						{ majorData && !!majorData.tuition_fee_per_semester &&  
							<div className="major-info-item mb-0">
							<MoneyOutlineIcon className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className="mii-label me-sm">{Variable.TUITION_FEE_SMT[ENV_NAME]}</div>
							<div className="mii-item">
								{!isNaN(majorData?.tuition_fee_per_semester)
									? `${getCurrencySymbol(majorData?.fees_currency || "IDR", getLocale(ENV_NAME))}${
											Number(majorData?.tuition_fee_per_semester || 0)?.toLocaleString(
												getLocale(ENV_NAME)
											) || "-"
									  }`
									: majorData?.tuition_fee_per_semester || "-"}
							</div>
						</div>
						}
						<div className="divider" />
						<div className="major-info-item">
							<GradCapIconOutline className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className="mii-label me-sm">{Variable.DEGREE_INFORMATION[ENV_NAME]}</div>
							<div className="mii-item">{DEGREE_OPT?.[majorData?.degree] || "-"}</div>
						</div>
						<div className="major-info-item">
							<CalendarBlankIcon className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className="mii-label me-sm">{Variable.STUDY_TIME[ENV_NAME]}</div>
							<div className="mii-item">{majorData?.study_time || "-"}</div>
						</div>
						<div className="major-info-item mb-0">
							<BookmarkIcon className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className="mii-label me-sm">{Variable.SKS[ENV_NAME]}</div>
							<div className="mii-item">{majorData?.sks || "-"}</div>
						</div>
						<div className="divider" />
						<div className="major-info-item">
							<StudentIconOutline className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className="mii-label me-sm">{Variable.STUDENT[ENV_NAME]}</div>
							<div className="mii-item">
								{majorData?.number_of_students || Variable.DATA_NOT_AVAILABLE[ENV_NAME]}
							</div>
						</div>
						<div className="major-info-item mb-0">
							<ChalkboardTeacherIcon className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className="mii-label me-sm">{Variable.LECTURER[ENV_NAME]}</div>
							<div className="mii-item">
								{majorData?.number_of_lecturers || Variable.DATA_NOT_AVAILABLE[ENV_NAME]}
							</div>
						</div>
						<div className="divider" />
						<div className="major-info-item">
							<ChromeIcon className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className={`mii-label me-sm ${ENV_NAME === "bhs" ? "font-italic" : ""}`}>
								{Variable.WEBSITE[ENV_NAME]}
							</div>
							<div className="mii-item">
								{majorData?.major_website_url ? (
									<Anchor href={majorData?.major_website_url || "-"} target="_blank">
										{majorData?.major_website_url || "-"}
									</Anchor>
								) : (
									"-"
								)}
							</div>
						</div>
						<div className="major-info-item">
							<EnvelopeIcon className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className={`mii-label me-sm ${ENV_NAME === "bhs" ? "font-italic" : ""}`}>
								{Variable.EMAIL[ENV_NAME]}
							</div>
							<div className="mii-item">
								{majorData?.major_email_address ? (
									<Anchor href={"mailto:" + majorData?.major_email_address || "-"} target="_blank">
										{majorData?.major_email_address || "-"}
									</Anchor>
								) : (
									"-"
								)}
							</div>
						</div>
						<div className="major-info-item mb-0">
							<PhoneIcon className="me-sm" width="2.4rem" height="2.4rem" stroke="#193560" />
							<div className="mii-label me-sm">{Variable.PHONE[ENV_NAME]}</div>
							<div className="mii-item">{majorData?.major_phone_number || "-"}</div>
						</div>
					</section>
					{majorData?.registration_administration_info && (
						<div className="bg-container">
							<section className="container detail-section mb-0">
								<h2 className="header text-center">{Variable.REGISTRATION_INFO[ENV_NAME]}</h2>
								<div
									className="general-tuition force-styling-wysiwyg"
									dangerouslySetInnerHTML={{
										__html: majorData?.registration_administration_info || "",
									}}
								/>
							</section>
						</div>
					)}
					{majorData?.study_fees && (
						<section className="container detail-section">
							<h2 className="header text-center">{Variable.STUDY_FEES[ENV_NAME]}</h2>
							<div
								className="study-fees force-styling-wysiwyg"
								dangerouslySetInnerHTML={{ __html: majorData?.study_fees || "" }}
							/>
						</section>
					)}
					{majorData?.curriculum && (
						<div className="bg-container">
							<section className="container detail-section mb-0">
								<h2 className="header text-center">{Variable.CURRICULUM[ENV_NAME]}</h2>
								<div
									className="force-styling-wysiwyg"
									dangerouslySetInnerHTML={{
										__html: majorData?.curriculum || "",
									}}
								/>
							</section>
						</div>
					)}
					{majorData?.major_achievement?.length > 0 && (
						<section className="container detail-section">
							<h2 className="header text-center">{Variable.ACHIEVEMENTS[ENV_NAME]}</h2>
							<Carousel
								showStatus={false}
								showIndicators={false}
								showThumbs={false}
								showArrows={false}
								selectedItem={achivementIdx}
								onChange={(index, _) => setAchievementIdx(index)}
								className="achievement-carousel"
								centerSlidePercentage={
									window.screen.width < 960 ? (window.screen.width < 450 ? 100 : 50) : 33
								}
								centerMode
								emulateTouch
							>
								{orderBy(majorData?.major_achievement, ["year"], ["desc"])?.map((i, idx) => (
									<div className="achievement-card" key={idx}>
										<div className="flex-all-center w-100 mb-std">
											<div className="ac-year me-3">
												<StarIcon
													fill="#F05974"
													stroke="transparent"
													width="1.2rem"
													height="1.2rem"
													className="me-xxs"
												/>
												{i?.year || "-"}
											</div>
											<div className="ac-line" />
										</div>
										<div className="mb-xxs">
											<strong className="ac-title">{i?.title || "-"}</strong>
										</div>
										<div
											className="force-styling-wysiwyg"
											dangerouslySetInnerHTML={{
												__html: i?.description || "-",
											}}
										/>
									</div>
								))}
							</Carousel>
							<CarouselIndicator
								onChangeIndex={setAchievementIdx}
								idx={achivementIdx}
								length={majorData?.major_achievement?.length}
							/>
						</section>
					)}
					{(majorData?.concentration_description || majorData?.concentration_items?.length > 0) && (
						<section className="container detail-section">
							<h2 className="header text-center">{Variable.CONCENTRATION[ENV_NAME]}</h2>
							<div
								className="mb-md force-styling-wysiwyg"
								dangerouslySetInnerHTML={{
									__html: majorData?.concentration_description || "",
								}}
							/>
							<div className="facilities-cont">
								{majorData?.concentration_items?.map((i, idx) => (
									<div className="facilities-cont-item no-before" key={idx}>
										{i}
									</div>
								))}
							</div>
						</section>
					)}
					{majorData?.facility?.length > 0 && (
						<div className="bg-container">
							<section className="container detail-section mb-0">
								<h2 className="header text-center">{Variable.FACILITIES[ENV_NAME]}</h2>
								<div className="facilities-cont">
									{majorData?.facility?.map((i, idx) => (
										<div className="facilities-cont-item" key={idx}>
											{i}
										</div>
									))}
								</div>
							</section>
						</div>
					)}
					{majorData?.career?.length > 0 && (
						<section className="container detail-section">
							<h2 className="header text-center">{Variable.CARRER_PATH[ENV_NAME]}</h2>
							<div className="career-desc mb-md text-center">{Variable.CARRER_PATH_DESC[ENV_NAME]}</div>
							<div className="career-cont">
								{majorData?.career?.map((i) => (
									<Link to={RoutePath.CAREER_DETAIL + "/" + i?.id} key={i?.id}>
										<button className="btn btn-career">
											{i?.career_name}
											<TopRightArrowIcon
												className="career-arrow"
												width="2.4rem"
												height="2.4rem"
												stroke="white"
											/>
										</button>
									</Link>
								))}
							</div>
						</section>
					)}
				</>
			)}
		</div>
	);
};

const mapStateToProps = (state) => {
	return {
		isAuthenticated: state.auth.isAuthenticated,
		ENV_NAME: state.auth.selectedEnvironment || "bhs",
		fetchingAddRemoveFavorites: state.auth.fetchingAddRemoveFavorites,
	};
};

const mapStateToDispatch = (dispatch) => {
	return {
		openLoginModal: () =>
			dispatch({
				type: ActionTypes.OPEN_LOGIN_MODAL,
			}),
		addRemoveFavorite: (action, type, id, onSuccess = null, onFail = null) =>
			dispatch({
				type: ActionTypes.ADD_REMOVE_FAVORITE,
				data: { action, type, id },
				onSuccess,
				onFail,
			}),
	};
};

export default connect(mapStateToProps, mapStateToDispatch)(withRouter(MajorDetail));
